import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useState } from 'react';
import { CategoryItem } from '../../contexts/CategoryContext';

interface SortableItemProps {
  cat: CategoryItem;
  idx: number;
  isDragging?: boolean;
}

function SortableItem({ cat, idx, isDragging }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSelfDragging,
  } = useSortable({ id: cat.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSelfDragging ? 0.3 : 1,
    zIndex: isSelfDragging ? 999 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-md px-3 py-2.5 border select-none ${
        isSelfDragging
          ? 'border-emerald-400 bg-emerald-50'
          : 'bg-white border-gray-200'
      }`}
    >
      {/* 드래그 핸들 */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-emerald-600 cursor-grab active:cursor-grabbing transition-colors rounded"
        title="드래그하여 순서 변경"
      >
        <i className="ri-draggable text-base"></i>
      </button>

      <span className="w-5 h-5 flex items-center justify-center text-xs font-bold text-gray-400 flex-shrink-0">
        {idx + 1}
      </span>

      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800 truncate">{cat.title}</p>
        {cat.isCustom && (
          <span className="text-xs text-emerald-600 font-medium">추가됨</span>
        )}
      </div>

      <i className="ri-menu-line text-gray-300 text-sm flex-shrink-0"></i>
    </div>
  );
}

function DragOverlayItem({ cat, idx }: { cat: CategoryItem; idx: number }) {
  return (
    <div className="flex items-center gap-2 rounded-md px-3 py-2.5 border border-emerald-400 bg-emerald-50 shadow-lg select-none">
      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-emerald-500">
        <i className="ri-draggable text-base"></i>
      </div>
      <span className="w-5 h-5 flex items-center justify-center text-xs font-bold text-emerald-500 flex-shrink-0">
        {idx + 1}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-emerald-700 font-medium truncate">{cat.title}</p>
        {cat.isCustom && (
          <span className="text-xs text-emerald-500 font-medium">추가됨</span>
        )}
      </div>
    </div>
  );
}

interface CategorySortPanelProps {
  categoryList: CategoryItem[];
  onReorder: (newList: CategoryItem[]) => void;
  onClose: () => void;
}

export default function CategorySortPanel({
  categoryList,
  onReorder,
  onClose,
}: CategorySortPanelProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  const activeItem = activeId
    ? categoryList.find((c) => c.id === activeId)
    : null;
  const activeIdx = activeId
    ? categoryList.findIndex((c) => c.id === activeId)
    : -1;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIdx = categoryList.findIndex((c) => c.id === active.id);
    const newIdx = categoryList.findIndex((c) => c.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    onReorder(arrayMove(categoryList, oldIdx, newIdx));
  };

  const dropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: { active: { opacity: '0.3' } },
    }),
  };

  return (
    <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <p className="text-sm font-medium text-gray-800 mb-1 flex items-center gap-2">
        <i className="ri-list-ordered text-gray-500"></i>
        카테고리 순서 변경
      </p>
      <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
        <i className="ri-draggable text-gray-400"></i>
        핸들을 드래그하여 원하는 위치로 이동하세요
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={categoryList.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {categoryList.map((cat, idx) => (
              <SortableItem key={cat.id} cat={cat} idx={idx} />
            ))}
          </div>
        </SortableContext>

        <DragOverlay dropAnimation={dropAnimation}>
          {activeItem ? (
            <DragOverlayItem cat={activeItem} idx={activeIdx} />
          ) : null}
        </DragOverlay>
      </DndContext>

      <button
        type="button"
        onClick={onClose}
        className="mt-3 w-full py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
      >
        닫기
      </button>
    </div>
  );
}
